import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import {
  User,
  Settings,
  Database,
  Shield,
  FileText,
  Wallet,
  Clock,
  MapPin,
  Server,
} from 'lucide-react';

interface AuditLog {
  id: string;
  action: string;
  category: 'user' | 'asset' | 'compliance' | 'settings' | 'wallet' | 'report' | 'system';
  actor: string;
  details: string;
  ipAddress: string;
  timestamp: Date;
  severity: 'info' | 'warning' | 'critical';
}

interface AuditLogDetailModalProps {
  log: AuditLog | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const getCategoryIcon = (category: AuditLog['category']) => {
  switch (category) {
    case 'user':
      return <User className='w-5 h-5' />;
    case 'asset':
      return <Database className='w-5 h-5' />;
    case 'compliance':
      return <Shield className='w-5 h-5' />;
    case 'settings':
      return <Settings className='w-5 h-5' />;
    case 'wallet':
      return <Wallet className='w-5 h-5' />;
    case 'report':
      return <FileText className='w-5 h-5' />;
    case 'system':
      return <Server className='w-5 h-5' />;
  }
};

const getSeverityColor = (severity: AuditLog['severity']) => {
  switch (severity) {
    case 'info':
      return 'bg-info/20 text-info border-info/30';
    case 'warning':
      return 'bg-warning/20 text-warning border-warning/30';
    case 'critical':
      return 'bg-destructive/20 text-destructive border-destructive/30';
  }
};

const formatDateTime = (date: Date) => {
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

export const AuditLogDetailModal: React.FC<AuditLogDetailModalProps> = ({
  log,
  open,
  onOpenChange,
}) => {
  if (!log) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-2xl mx-4 sm:mx-auto'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-3'>
            <div className={`p-2 rounded-lg border ${getSeverityColor(log.severity)}`}>
              {getCategoryIcon(log.category)}
            </div>
            <div>
              <h3 className='text-lg font-semibold'>{log.action}</h3>
              <div className='flex items-center gap-2 mt-1'>
                <Badge variant='outline' className='text-xs capitalize'>
                  {log.category}
                </Badge>
                <Badge variant='secondary' className='text-xs capitalize'>
                  {log.severity}
                </Badge>
              </div>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className='space-y-6'>
          {/* Details */}
          <div>
            <h4 className='font-medium mb-2'>Details</h4>
            <p className='text-muted-foreground bg-muted/50 p-3 rounded-lg'>{log.details}</p>
          </div>

          <Separator />

          {/* Metadata */}
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
            <div className='space-y-4'>
              <div className='flex items-center gap-3'>
                <User className='w-4 h-4 text-muted-foreground' />
                <div>
                  <p className='text-sm font-medium'>Actor</p>
                  <p className='text-sm text-muted-foreground'>{log.actor}</p>
                </div>
              </div>

              <div className='flex items-center gap-3'>
                <MapPin className='w-4 h-4 text-muted-foreground' />
                <div>
                  <p className='text-sm font-medium'>IP Address</p>
                  <p className='text-sm text-muted-foreground font-mono'>{log.ipAddress}</p>
                </div>
              </div>
            </div>

            <div className='space-y-4'>
              <div className='flex items-center gap-3'>
                <Clock className='w-4 h-4 text-muted-foreground' />
                <div>
                  <p className='text-sm font-medium'>Timestamp</p>
                  <p className='text-sm text-muted-foreground'>{formatDateTime(log.timestamp)}</p>
                </div>
              </div>

              <div className='flex items-center gap-3'>
                <div
                  className={`w-4 h-4 rounded-full border-2 ${getSeverityColor(log.severity)}`}
                />
                <div>
                  <p className='text-sm font-medium'>Severity</p>
                  <p className='text-sm text-muted-foreground capitalize'>{log.severity}</p>
                </div>
              </div>
            </div>
          </div>

          <Separator />

          {/* Log ID */}
          <div className='text-xs text-muted-foreground'>
            <span className='font-medium'>Log ID:</span> {log.id}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
